/**
 * Assembles all CSS for the iframe document: global base, theme variables,
 * section CSS, overrides, and editing styles.
 */

import { scopeCSS } from '../shared/css-scoper.js';
import { buildButtonEffectRegistry, BUTTON_SEL } from './animation-injector.js';
import { DECORATIVE_CLASSES } from '../shared/element-kind.js';
import { FORM_SHAPES, FORM_STYLES, FORM_SUBMIT_STYLES } from '../shared/forms-presets-data.js';
import {
  rewriteCssAssetUrlsPreferVendored,
  getVendoredAssetMapSync,
  normalizeSectionTypography,
  stripTemplateComponentRules,
  hasStateFormRootRules,
  getTemplateFontReport,
  TEMPLATE_BUTTON_CLASSES,
  TEMPLATE_CARD_CLASSES,
  TEMPLATE_INPUT_CLASSES,
  TEMPLATE_PILL_CLASSES
} from '../shared/template-support-css.js';
import { FONT_POLICY_VENDORED } from '../shared/template-font-rules.js';
import { isSharedCategory } from '../shared/shared-section-types.js';
import { formatImagePct, formatImageScale } from '../editing/image-runtime-css.js';
import { detectMultiItemGroups } from '../editing/section-group-detection.js';
import { normalizeThemeTokens } from '../shared/theme-tokens.js';
import { scaleCssLengthExpression } from '../editing/text-size-scale.js';

export const INLINE_TEXT_COLOR_CSS = `
.pb-inline-color-accent {
  color: var(--primary, #ff6a3e) !important;
}
.pb-inline-color-heading {
  color: var(--headerColor, #1a1a1a) !important;
}
.pb-inline-color-body {
  color: var(--bodyTextColor, #4e4b66) !important;
}
`;

export function assembleThemeCSS(theme) {
  if (!theme || typeof theme !== 'object') return '';
  const vars = Object.entries(
    normalizeThemeTokens(theme, { preserveExisting: true, includeDerived: true })
  )
    .map(([key, value]) => `  ${key}: ${value};`)
    .join('\n');
  return `:root {\n${vars}\n}`;
}

function shouldStripSectionComponentRules(section, flag, options = {}) {
  if (!flag) return false;
  if (!section || isSharedCategory(section.category)) return false;

  const primaryDomain =
    typeof options.sourceTemplateDomain === 'string' ? options.sourceTemplateDomain.trim() : '';
  if (!primaryDomain) return true;

  const sectionDomain =
    typeof section.templateSiteName === 'string' ? section.templateSiteName.trim() : '';
  return sectionDomain !== primaryDomain;
}

function stripTemplateComponentRulesForSection(css, section, options = {}) {
  let nextCSS = css;
  if (shouldStripSectionComponentRules(section, options.stripButtons, options)) {
    nextCSS = stripTemplateComponentRules(nextCSS, TEMPLATE_BUTTON_CLASSES);
  }
  if (shouldStripSectionComponentRules(section, options.stripCards, options)) {
    nextCSS = stripTemplateComponentRules(nextCSS, TEMPLATE_CARD_CLASSES);
  }
  if (shouldStripSectionComponentRules(section, options.stripInputs, options)) {
    nextCSS = stripTemplateComponentRules(nextCSS, TEMPLATE_INPUT_CLASSES);
  }
  if (shouldStripSectionComponentRules(section, options.stripPills, options)) {
    nextCSS = stripTemplateComponentRules(nextCSS, TEMPLATE_PILL_CLASSES);
  }
  return nextCSS;
}

export function assembleSectionsCSS(sections, options = {}) {
  const seen = new Set();
  const blocks = [];
  for (const section of sections) {
    if (!section.css) continue;
    const key = section.cssDedupKey || section.catalogKey || section.sectionId;
    if (seen.has(key)) continue;
    seen.add(key);
    // A VENDORED BYTE WINS; a hotlink is the fallback.
    //
    // This used to be `rewriteCssAssetUrls` alone: every root-relative url(/…)
    // became `https://<templateSiteName>/…`, a live request to somebody else's
    // server on every render, forever. abshillbillygyros.com is what that costs
    // — its bare host stopped resolving, its hero background silently became
    // nothing, and the `#f5f3f3` hero text rendered white on white while the
    // image sat vendored on disk, unread, because nothing here ever asked.
    //
    // Sections without a templateSiteName (synthetic catalog stitches) are left
    // unchanged — their assets are expected to be served by the builder itself.
    let cssText = section.templateSiteName
      ? rewriteCssAssetUrlsPreferVendored(
          section.css,
          section.templateSiteName,
          getVendoredAssetMapSync(section.templateSiteName)
        )
      : section.css;
    cssText = stripTemplateComponentRulesForSection(cssText, section, options);
    // Wrap hardcoded font-family declarations with var(--pb-heading-font, ...)
    // or var(--pb-body-font, ...) so the project theme wins over a section
    // author's local font choice. Opt out per-section via preserveTypography.
    // Limitation: effectively catalog-level here — assembleSectionsCSS dedupes
    // by catalog key, so the first instance encountered for a catalog wins.
    // IF-4: a site under font policy v1 vendors and keeps its OWN faces, and
    // "a template's own fonts are consumed, not substituted" (builder/CLAUDE.md).
    // Wrapping its stacks in var(--pb-body-font, …) hands the theme font the win
    // anyway — the vars are emitted unconditionally — so the template renders in
    // a typeface it never declared. On chadwickschool.org that substitution is
    // what wrapped every subnav link to two lines (18px -> 36px) and shifted the
    // header down 18px per row, for 220,097 builder-context deltas. Gated on the
    // policy, which is on only for a site absent from the committed catalog, so
    // the ~200 published templates keep theme substitution exactly as before.
    // A policy site whose families were ALL dropped has no fonts of its own to
    // consume, so the theme still applies there.
    const fontReport = section.templateSiteName
      ? getTemplateFontReport(section.templateSiteName)
      : null;
    const templateOwnsItsFonts =
      Number(fontReport?.policy || 0) >= FONT_POLICY_VENDORED &&
      (fontReport?.kept?.length || 0) > 0;
    // AND THE THEME MUST BE A CHOICE BEFORE IT OUTRANKS THE TEMPLATE.
    //
    // `templateOwnsItsFonts` above only protects a site whose faces are
    // VENDORED — its own comment says a policy site whose families were all
    // dropped still gets substituted. That leaves every Google-hosted template
    // unprotected, and the substitution there is not a user preference winning
    // over a section author: on load `applyTemplateFonts` DERIVES the theme
    // from the template, so what wins is one of the template's own faces
    // overwriting the others. abshillbillygyros.com declares Raleway, Slackey
    // and Rock Salt; Raleway took all three and the hero lost its display face.
    //
    // So the rewrite waits for intent. `fontSource` is 'template' until someone
    // picks a font, and this is a NO-OP for the 366 single-font templates —
    // their declared family IS the derived one, so `var(--pb-heading-font, X)`
    // with `--pb-heading-font: X` already rendered identically.
    const themeFontIsDerived = options.fontSource === 'template';
    if (!section.preserveTypography && !templateOwnsItsFonts && !themeFontIsDerived) {
      cssText = normalizeSectionTypography(cssText);
    }
    blocks.push(`/* ${key} */\n${cssText}`);
  }
  return blocks.join('\n\n');
}

function camelToKebab(str) {
  return str.replace(/[A-Z]/g, (m) => '-' + m.toLowerCase());
}

/**
 * Normalize a stored `columnCount` override to a positive integer.
 * Returns null when the value is missing or unparseable so the caller can
 * skip emission entirely (no empty rule, no `repeat(NaN, 1fr)`).
 */
function _normalizeColumnCount(value) {
  if (value == null || value === '') return null;
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n) || n < 1) return null;
  return n;
}

/**
 * How far a decorative shape hangs past the bottom of its section, as a
 * percentage of the shape's own height. '' / null means "off".
 */
export function _normalizeDecorativeBleed(value) {
  if (value == null || value === '') return null;
  const n = Number(String(value).replace('%', '').trim());
  if (!Number.isFinite(n) || n <= 0) return null;
  return `${Math.min(100, Math.round(n))}%`;
}

/**
 * Let a decorative SVG spill over the following section.
 *
 * `insert-decorative-svg.js` stamps `overflow: hidden; isolation: isolate` onto
 * the host section whenever a decorative SVG is added, and the SVG itself is
 * `position: absolute; bottom: 0`. Between them, a wave meant to overlap the
 * next section is guillotined at the section boundary and could not paint over
 * it even if it escaped. There was no way to opt out.
 *
 * Three things have to change together, which is why this is one builder:
 *   - `overflow: visible` so the shape is not clipped;
 *   - `isolation: auto` + an explicit stacking context, so it paints ABOVE the
 *     next section (the canvas gives wrappers a descending z-index ladder, but
 *     that is preview-only CSS — an export has bare sibling sections, where the
 *     LATER one wins by default);
 *   - a `translateY`, because `bottom: 0` means the shape starts fully inside
 *     the box. Removing the clip alone changes nothing visible.
 *
 * Emitted identically by the preview and export assemblers.
 */
export function buildDecorativeBleedCSS(scope, bleed) {
  const offset = _normalizeDecorativeBleed(bleed);
  if (!offset || !scope) return '';
  // The shape set is DERIVED from the one decorative-class list, not copied:
  // the panel lists template-native decoratives (`.cs-wave`, `.cs-divider`)
  // alongside builder-inserted `.pb-decorative-svg`, so a bleed control that
  // only moved the latter would appear to do nothing on a template's own wave.
  const shapes = DECORATIVE_CLASSES.map((name) => `${scope} .${name}`).join(',\n');
  // DS-6: the TOP-edge counterpart. `decorativeBleed` was bottom-only —
  // `translateY(+offset)` — so a shape pinned to the top edge had no escape
  // from the `overflow: hidden` the insert stamps, and the control silently did
  // nothing for it. A top shape must travel the other way, so the sign flips.
  //
  // A SECOND, MORE SPECIFIC RULE rather than a branch: a section can hold both
  // a top shape and a bottom one, and one `transform` for both would move them
  // the same way. `.pb-decorative-top` is a CLASS (G7) precisely so this
  // selector survives `stripAllBuilderAttrs` into the export.
  const topShapes = DECORATIVE_CLASSES.map((name) => `${scope} .${name}.pb-decorative-top`).join(
    ',\n'
  );
  return [
    `${scope} { overflow: visible !important; isolation: auto !important; position: relative !important; z-index: 2 !important; }`,
    `${shapes} { transform: translateY(${offset}) !important; }`,
    `${topShapes} { transform: translateY(-${offset}) !important; }`
  ].join('\n');
}

/**
 * Build the desktop-breakpoint column-count override CSS. Selector specificity
 * (0,2,1) + `!important` beats CodeStitch's authored `[data-pb-section] .cs-item
 * { width: 47% }` / `grid-column: span 4` rules. Gap is intentionally left to
 * the template — desktop spacing per-template already looks right; we only
 * force the cell template and reset cs-item sizing.
 */
function _buildColumnCountCSS(scope, groupSelector, n) {
  return [
    '@media (min-width: 64rem) {',
    `  ${scope} ${groupSelector} {`,
    `    grid-template-columns: repeat(${n}, 1fr) !important;`,
    '    display: grid !important;',
    '  }',
    `  ${scope} ${groupSelector} > .cs-item {`,
    '    width: auto !important;',
    '    grid-column: auto !important;',
    '    flex-basis: auto !important;',
    '  }',
    '}'
  ].join('\n');
}

function formatNumericPx(value) {
  if (!Number.isFinite(value)) return '';
  return `${parseFloat(value.toFixed(4))}px`;
}

export function resolveFontSizeCSSValue(value) {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || '';
  }
  if (!value || typeof value !== 'object') return '';

  if (typeof value.legacyValue === 'string') {
    const trimmed = value.legacyValue.trim();
    if (trimmed) return trimmed;
  }

  if (Number.isFinite(value.anchorPx) && Number.isFinite(value.ratio)) {
    const scaledExpression = scaleCssLengthExpression(value.anchor, Number(value.ratio));
    if (scaledExpression) return scaledExpression;
    return formatNumericPx(Number(value.anchorPx) * Number(value.ratio));
  }

  if (typeof value.value === 'string') {
    // Backward compatibility with older object form.
    const trimmed = value.value.trim();
    return trimmed || '';
  }

  return '';
}

export function assembleSectionOverrideCSS(section, { includeCustomCSS = true } = {}) {
  const blocks = [];
  if (!section?.overrides) return '';

  const { backgroundColor, paddingTop, paddingBottom, marginTop, marginBottom, customCSS } =
    section.overrides;
  // Overrides represent explicit user intent and always win authored
  // template declarations, including template-level !important rules.
  const sImp = () => ' !important';
  const innerRules = [];
  if (backgroundColor)
    innerRules.push(`background-color: ${backgroundColor}${sImp('backgroundColor')};`);
  if (paddingTop) innerRules.push(`padding-top: ${paddingTop}${sImp('paddingTop')};`);
  if (paddingBottom) innerRules.push(`padding-bottom: ${paddingBottom}${sImp('paddingBottom')};`);
  if (innerRules.length > 0) {
    blocks.push(
      `[data-pb-instance="${section.instanceId}"] > *:first-child { ${innerRules.join(' ')} }`
    );
  }
  const wrapperRules = [];
  if (marginTop) wrapperRules.push(`margin-top: ${marginTop}${sImp('marginTop')};`);
  if (marginBottom) wrapperRules.push(`margin-bottom: ${marginBottom}${sImp('marginBottom')};`);
  if (wrapperRules.length > 0) {
    blocks.push(`[data-pb-instance="${section.instanceId}"] { ${wrapperRules.join(' ')} }`);
  }
  if (includeCustomCSS && customCSS) {
    const scoped = scopeCSS(customCSS, `[data-pb-instance="${section.instanceId}"]`);
    if (scoped) {
      blocks.push(`/* custom: ${section.instanceId} */\n${scoped}`);
    }
  }

  // Column-count override — reflows a detected `.cs-*-group > .cs-item`
  // layout to N equal columns at desktop breakpoint. `!important` beats
  // CodeStitch's authored width / grid-column / flex-basis rules. Only
  // emitted when the section actually contains a multi-item group; the
  // override is silently ignored otherwise (prevents stale `columnCount`
  // values from surfacing on sections that no longer match the layout).
  // Decorative bleed targets the section ROOT (`> *:first-child`), which is the
  // element insert-decorative-svg.js put `overflow: hidden` on.
  const bleedCSS = buildDecorativeBleedCSS(
    `[data-pb-instance="${section.instanceId}"] > *:first-child`,
    section.overrides.decorativeBleed
  );
  if (bleedCSS) blocks.push(bleedCSS);

  const columnCount = _normalizeColumnCount(section.overrides.columnCount);
  if (columnCount) {
    const group = detectMultiItemGroups(section);
    if (group) {
      blocks.push(
        _buildColumnCountCSS(
          `[data-pb-instance="${section.instanceId}"]`,
          group.groupSelector,
          columnCount
        )
      );
    }
  }

  // Element-level overrides (image resize, text size, hide, etc.)
  if (section.overrides && section.overrides.elements) {
    // Properties that need to target the <picture> parent (not the <img>) for
    // CodeStitch layouts where images are absolutely positioned inside containers
    // with overflow:hidden. These are split out and applied to the picture wrapper.
    const PICTURE_PARENT_PROPS = new Set(['aspectRatio', 'borderRadius', 'boxShadow']);

    // Namespaced picture-layout props → CSS on <picture> parent only (no overflow:hidden)
    const PICTURE_LAYOUT_MAP = {
      pictureWidth: 'width',
      pictureMaxWidth: 'max-width',
      pictureMarginTop: 'margin-top',
      pictureMarginRight: 'margin-right',
      pictureMarginBottom: 'margin-bottom',
      pictureMarginLeft: 'margin-left',
      picturePaddingTop: 'padding-top',
      picturePaddingRight: 'padding-right',
      picturePaddingBottom: 'padding-bottom',
      picturePaddingLeft: 'padding-left',
      pictureLeft: 'left'
    };

    for (const [eid, props] of Object.entries(section.overrides.elements)) {
      const rules = [];
      const svgRules = [];
      const pictureRules = [];
      const pictureLayoutRules = [];
      const imgFixRules = [];
      let hasPictureProps = false;
      const SVG_STRONG_PROPS = new Set([
        'width',
        'height',
        'minWidth',
        'minHeight',
        'maxWidth',
        'maxHeight'
      ]);

      // Non-CSS override keys — applied as HTML attributes, not CSS rules.
      // className/elementId remain skipped until button/image panels complete
      // HTML-mutation migration (text panel is migrated in A2/B plan).
      // imageHover is HTML-assembly-only: the engaged attribute lives on
      // the <img> and the hover rules ship in IMAGE_RUNTIME_CSS.
      // __important is the Phase 6 escalation metadata map — never CSS.
      const SKIP_PROPS = new Set([
        'hidden',
        'preserveAspectRatio',
        'className',
        'elementId',
        'imageHover',
        'imageEffect',
        '__important',
        // EA-7: entrance is MARKUP, never element CSS — this emitter appends
        // !important and golden G4 pins the entrance stylesheet at zero.
        'revealPreset',
        'revealDelay'
      ]);

      // Element overrides always win authored template declarations.
      const imp = () => ' !important';

      for (const [prop, val] of Object.entries(props)) {
        if (SKIP_PROPS.has(prop)) continue;

        // Format-painter snapshot: nested object of computed-style values.
        // Each entry is emitted as `${kebab}: ${val} !important;` so the
        // painted value beats the section's authored CSS (e.g. nth-child
        // selectors). Source values come from getComputedStyle on the
        // donor element.
        if (prop === 'paintedStyles') {
          if (val && typeof val === 'object') {
            for (const [sp, sv] of Object.entries(val)) {
              if (typeof sv !== 'string' || sv === '') continue;
              rules.push(`${camelToKebab(sp)}: ${sv} !important;`);
            }
          }
          continue;
        }

        // Picture-layout props → separate accumulator (no overflow:hidden side effect)
        if (PICTURE_LAYOUT_MAP[prop]) {
          pictureLayoutRules.push(`${PICTURE_LAYOUT_MAP[prop]}: ${val} !important;`);
          continue;
        }

        // Button type → maps to multiple CSS properties
        if (prop === 'buttonType') {
          if (val === 'outline') {
            rules.push(
              'background: transparent !important;',
              'border: 2px solid currentColor !important;'
            );
          } else if (val === 'ghost') {
            rules.push('background: transparent !important;', 'border: none !important;');
          }
          continue;
        }
        // Button color → applies as bg (solid) or text color (outline/ghost)
        if (prop === 'buttonBgColor') {
          const btnType = props.buttonType || 'solid';
          if (btnType === 'solid') {
            rules.push(`background-color: ${val}${imp('buttonBgColor')};`);
          } else {
            rules.push(`color: ${val}${imp('buttonBgColor')};`);
          }
          continue;
        }

        if (prop === 'fontSize') {
          const cssVal = resolveFontSizeCSSValue(val);
          if (cssVal) rules.push(`font-size: ${cssVal}${imp('fontSize')};`);
          continue;
        }

        // Image position overrides → emit CSS vars only. The composed
        // `transform:` rule lives in IMAGE_RUNTIME_CSS gated by the
        // `img[data-image-edit]` engaged attribute (set by HTML assembly),
        // so untouched images keep their original transform behavior.
        if (prop === 'imageTx') {
          const n = Number(val);
          if (Number.isFinite(n) && n !== 0) {
            rules.push(`--pb-image-tx-pct: ${formatImagePct(n)} !important;`);
          }
          continue;
        }
        if (prop === 'imageTy') {
          const n = Number(val);
          if (Number.isFinite(n) && n !== 0) {
            rules.push(`--pb-image-ty-pct: ${formatImagePct(n)} !important;`);
          }
          continue;
        }
        if (prop === 'imageScale') {
          const n = Number(val);
          if (Number.isFinite(n) && n !== 1) {
            rules.push(`--pb-image-scale: ${formatImageScale(n)} !important;`);
          }
          continue;
        }

        if (PICTURE_PARENT_PROPS.has(prop)) {
          pictureRules.push(`${camelToKebab(prop)}: ${val} !important;`);
          hasPictureProps = true;
        } else {
          const cssProp = camelToKebab(prop);
          rules.push(`${cssProp}: ${val}${imp(prop)};`);
          if (SVG_STRONG_PROPS.has(prop)) {
            svgRules.push(`${cssProp}: ${val} !important;`);
          }
        }
      }
      if (props.hidden) {
        rules.push('display: none !important;');
      }

      const scope = `[data-pb-instance="${section.instanceId}"]`;
      // Double the eid attr selector to boost specificity above CodeStitch's
      // [data-pb-section] .cs-picture img { ... } rules (0,2,1).
      // [data-pb-instance][data-pb-eid][data-pb-eid] = (0,3,0) > (0,2,1).
      const eidSel = `[data-pb-eid="${eid}"][data-pb-eid="${eid}"]`;

      if (rules.length) {
        blocks.push(`${scope} ${eidSel} { ${rules.join(' ')} }`);
      }
      if (svgRules.length) {
        blocks.push(`${scope} svg${eidSel} { ${svgRules.join(' ')} }`);
      }

      // For image-visual props, emit rules on the <picture> parent so they work
      // with CodeStitch's abs-positioned image + overflow:hidden pattern.
      // Also override the <img> positioning so aspect-ratio takes effect.
      if (hasPictureProps) {
        pictureRules.push('overflow: hidden !important;');

        // When aspect-ratio is set, the <picture> needs to be in flow (not abs-positioned)
        // so the aspect-ratio can define its height.
        if (props.aspectRatio) {
          pictureRules.push('position: relative !important;', 'height: auto !important;');
        }

        blocks.push(`${scope} picture:has(> ${eidSel}) { ${pictureRules.join(' ')} }`);
        // Also apply directly to the eid element for standalone <img> (no <picture>)
        blocks.push(
          `${scope} :not(picture) > ${eidSel}, ${scope} > ${eidSel} { ${pictureRules.join(' ')} }`
        );

        // When aspect-ratio is set, override the abs-positioning on the <img>.
        // Uses doubled attribute selector to boost specificity above CodeStitch's
        // [data-pb-section] .cs-picture img { position: absolute } rules.
        if (props.aspectRatio) {
          imgFixRules.push(
            'position: relative !important;',
            'width: 100% !important;',
            'height: 100% !important;'
          );
          blocks.push(`${scope} ${eidSel} { ${imgFixRules.join(' ')} }`);
        }
      }

      // Picture-layout props target <picture> parent only — no standalone fallback
      // to avoid stale override leaks when picture wrapper is removed.
      if (pictureLayoutRules.length) {
        blocks.push(`${scope} picture:has(> ${eidSel}) { ${pictureLayoutRules.join(' ')} }`);
      }
    }
  }

  const responsiveQueries = {
    mobile: '(max-width: 47.999rem)',
    tablet: '(min-width: 48rem) and (max-width: 63.999rem)',
    desktop: '(min-width: 64rem)'
  };
  const responsive = section.overrides.responsive;
  if (responsive && typeof responsive === 'object') {
    for (const viewport of ['desktop', 'tablet', 'mobile']) {
      const bucket = responsive[viewport];
      if (!bucket || typeof bucket !== 'object') continue;
      const css = assembleSectionOverrideCSS(
        { ...section, overrides: bucket },
        { includeCustomCSS: false }
      );
      if (!css) continue;
      const indented = css
        .split('\n')
        .map((line) => `  ${line}`)
        .join('\n');
      blocks.push(`@media ${responsiveQueries[viewport]} {\n${indented}\n}`);
    }
  }
  return blocks.join('\n\n');
}

export function assembleOverridesCSS(sections) {
  return (sections || [])
    .map((section) => assembleSectionOverrideCSS(section))
    .filter(Boolean)
    .join('\n\n');
}

/**
 * @param {object} buttonStyles  state.buttonStyles
 * @param {string} [buttonSelector]  the project's derived button selector.
 *   Omitted → the CodeStitch default, which is what every caller without a
 *   project in scope gets and what keeps historical output byte-identical.
 */
export function assembleButtonCSS(buttonStyles, buttonSelector) {
  if (!buttonStyles) return '';
  const sel = buttonSelector || BUTTON_SEL;
  const rules = [];
  const beforeRules = [];
  if (buttonStyles.borderRadius != null) {
    rules.push(`border-radius: ${buttonStyles.borderRadius};`);
    beforeRules.push(`border-radius: ${buttonStyles.borderRadius};`);
  }
  if (buttonStyles.padding) rules.push(`padding: ${buttonStyles.padding};`);
  if (buttonStyles.backgroundColor)
    rules.push(`background-color: ${buttonStyles.backgroundColor};`);
  if (buttonStyles.textColor) rules.push(`color: ${buttonStyles.textColor};`);

  const effect = buttonStyles.hoverEffect || 'none';
  const effectDef = buildButtonEffectRegistry(sel)[effect];

  // If no static rules and no effect, nothing to emit
  if (!rules.length && !beforeRules.length && effect === 'none') return '';

  let css = '';
  if (rules.length) css += `${sel} { ${rules.join(' ')} }\n`;
  if (beforeRules.length && effect === 'none') {
    // Only emit default ::before overrides when no effect replaces them
    css += `${sel}::before { ${beforeRules.join(' ')} }`;
  }

  // Append effect CSS (each effect handles its own ::before suppression)
  if (effectDef?.css) css += '\n' + effectDef.css;

  return css;
}

// Scoped to form controls inside the rendered page. The class-aware branches
// match the CodeStitch form fields; the generic branches keep custom/user forms
// editable without needing a catalog-specific class.
const FORM_FIELD_SEL =
  'main form :is(input.cs-input, select.cs-select, textarea.cs-textarea, input:not([type="checkbox"]):not([type="radio"]):not([type="hidden"]):not([type="submit"]):not([type="button"]), select, textarea):not(.g-recaptcha-response)';

// Includes the common CodeStitch button classes so submit buttons can diverge
// from global page CTA styles. This style block is emitted after button styles.
const FORM_SUBMIT_SEL =
  'main form :is(.cs-button-solid, .cs-button-transparent, button[type="submit"], button:not([type]), input[type="submit"]):not(.cs-btn-custom)';

function _declString(obj) {
  return Object.entries(obj)
    .map(([key, value]) => `${key}: ${value} !important;`)
    .join(' ');
}

// hasStateFormRootRules moved to template-support-css.js to break an import
// cycle (see resolveComponentStripPlan there); re-exported for API stability.
export { hasStateFormRootRules };

export function assembleFormCSS(formStyles) {
  if (!formStyles) return '';
  const fieldDecls = {};
  const submitDecls = {};
  const shape = FORM_SHAPES.find((preset) => preset.id === formStyles.shape);
  const style = FORM_STYLES.find((preset) => preset.id === formStyles.style);
  const submit = FORM_SUBMIT_STYLES.find((preset) => preset.id === formStyles.submit);

  if (shape) Object.assign(fieldDecls, shape.fieldRules);
  if (style) Object.assign(fieldDecls, style.fieldRules);
  if (submit) Object.assign(submitDecls, submit.submitRules);

  const blocks = [];
  if (Object.keys(fieldDecls).length)
    blocks.push(`${FORM_FIELD_SEL} { ${_declString(fieldDecls)} }`);
  if (Object.keys(submitDecls).length)
    blocks.push(`${FORM_SUBMIT_SEL} { ${_declString(submitDecls)} }`);
  return blocks.join('\n');
}

/* Identity pills (edit / selected / hover) are generated pseudo-elements on the
 * pill host. ::after is the default slot, but templates own ::after on plenty of
 * real elements — CodeStitch top bars build their column dividers out of an
 * in-flow `.cs-item:after` (1px wide, clamp() side margins) — and overriding it
 * pulls that box out of flow, so every sibling before it reflows the moment the
 * item is hovered or selected. The iframe bridge measures each host and stamps
 * data-pb-pill-slot only when ::after is already taken: "before" moves the pill
 * to ::before, "none" means both are taken and the parent overlay draws the tag
 * instead. A host with a free ::after carries no attribute at all. */
const PILL_HOSTS = [
  { sel: '[data-pb-editable="text"].pb-editing[data-pb-edit-pill]', attr: 'data-pb-edit-pill' },
  { sel: '[data-pb-selected-label]:not(.pb-editing)', attr: 'data-pb-selected-label' },
  {
    sel: '[data-pb-hover-label]:hover:not([data-pb-selected-label]):not([data-pb-selected-target]):not(.pb-editing)',
    attr: 'data-pb-hover-label',
    // The only :hover-driven pill. On iOS a tap latches :hover until the user
    // taps something else, so this one nameplate would stick to a section that
    // is no longer hovered — and no JS is involved, so none of P19's reporter
    // work can clear it. Its CONTENT rule is emitted behind a hover query
    // instead; without `content` the pseudo-element never generates, so the
    // shared styling above can stay one list for all three hosts.
    hoverOnly: true
  }
];
const PILL_SLOTS = [
  { guard: ':not([data-pb-pill-slot])', pseudo: '::after' },
  { guard: '[data-pb-pill-slot="before"]', pseudo: '::before' }
];
const PILL_SELECTORS = PILL_SLOTS.flatMap((slot) =>
  PILL_HOSTS.map((host) => `${host.sel}${slot.guard}${slot.pseudo}`)
).join(',\n');
/* A pill sits above its host, so a host near the top of the canvas frame has it
 * drawn off-viewport and clipped in half. CSS cannot see the viewport edge, so
 * the bridge measures the host and stamps data-pb-pill-flip="below"; these rules
 * drop the pill under the host instead. One attribute more specific than the
 * base rule above AND declared after it, so it wins on both counts. */
const PILL_FLIP_SELECTORS = PILL_SLOTS.flatMap((slot) =>
  PILL_HOSTS.map((host) => `${host.sel}[data-pb-pill-flip="below"]${slot.guard}${slot.pseudo}`)
).join(',\n');
const contentRuleFor = (host, slot) =>
  `${host.sel}${slot.guard}${slot.pseudo} { content: attr(${host.attr}); }`;

const PILL_CONTENT_RULES = PILL_SLOTS.flatMap((slot) =>
  PILL_HOSTS.filter((h) => !h.hoverOnly).map((host) => contentRuleFor(host, slot))
).join('\n');

/** Hover-driven pill content, gated so a latched iOS :hover cannot render it. */
const PILL_HOVER_CONTENT_RULES = `@media (hover: hover) and (pointer: fine) {
${PILL_SLOTS.flatMap((slot) =>
  PILL_HOSTS.filter((h) => h.hoverOnly).map((host) => `  ${contentRuleFor(host, slot)}`)
).join('\n')}
}`;

export const EDITING_CSS = `
/* P6 Ship panel: hovering a findings row outlines its target WITHOUT scrolling.
   An attribute-driven pseudo-outline rather than a parent-overlay box, per the
   glass-overlay rule — identity-and-state visuals live in the iframe so they
   inherit the real layout box with no IPC latency. */
[data-pb-audit-highlight] {
  outline: 2px solid var(--pb-accent, #4f46e5) !important;
  outline-offset: 2px !important;
  transition: outline-color 120ms ease-out;
}
@media (prefers-reduced-motion: reduce) {
  [data-pb-audit-highlight] { transition: none; }
}
/* The frame's tokens. tokens.css does not reach this document, so the block below declares the
   tokens these rules read: generated from tokens.css's base :root (SYS-10, C-38), never copied.
   The builder's other registers do not reach the frame; C-38 records that as a gap. */
:root {
  /* sys-iframe-tokens: generated from builder/styles/tokens.css by sys-iframe-tokens.mjs --write. Do not edit. */
  --pb-accent: #0078d4;
  --pb-on-accent: #ffffff;
  --pb-accent-section: #8B5CF6;
  --pb-accent-section-subtle: rgba(139, 92, 246, 0.24);
  --pb-accent-section-subtle: color-mix(in srgb, var(--pb-accent-section) 24%, transparent);
  --pb-accent-muted: rgba(0, 120, 212, 0.08);
  --pb-accent-muted: color-mix(in srgb, var(--pb-accent) 8%, transparent);
  --pb-accent-glow: rgba(0, 120, 212, 0.2);
  --pb-accent-ai: #14b8a6;
  --pb-accent-glow: color-mix(in srgb, var(--pb-accent) 20%, transparent);
  --pb-font-sm: 11px;
  --pb-radius-sm: 4px;
  --pb-radius-chip: 10px;
  --pb-canvas-radius: var(--pb-radius-chip);
  --pb-space-1: 2px;
  --pb-space-4: 8px;
  --pb-edit-outline: rgba(0, 120, 212, 0.4);
  --pb-edit-outline-active: var(--pb-accent);
  /* sys-iframe-tokens: end */
}

[data-pb-editable="image"].cs-graphic,
[data-pb-editable="svg"].cs-graphic { pointer-events: none; }
[data-pb-editable="image"].cs-graphic.pb-editing,
[data-pb-editable="svg"].cs-graphic.pb-editing { pointer-events: auto; }
/* A drag starts life as a real pointerdown on a card's text, so the browser
   opens a text selection and paints it blue across the row as the pointer
   travels. preventDefault on pointermove cannot undo that — selection is
   initiated by the mousedown compatibility event, which the drag deliberately
   does not cancel (a click still has to reach links and the text editor). So it
   is suppressed for the duration of the drag instead, keyed on the attribute
   the engine already stamps on <html>. Declared BEFORE the text-editor's
   "user-select: text !important" rule on purpose: the two can never coexist
   (_findReorderHandle refuses while an editable is active), and if they somehow
   did, the person typing should win. */
[data-pb-reorder-active],
[data-pb-reorder-active] * {
  -webkit-user-select: none !important;
  user-select: none !important;
}
[data-pb-reorder-source] {
  opacity: 0.15;
  filter: blur(2px);
  pointer-events: none;
  transition: opacity 120ms ease-out, filter 120ms ease-out;
}
@media (prefers-reduced-motion: reduce) {
  [data-pb-reorder-source] { transition: none; opacity: 0; filter: none; }
}
.pb-reorder-ghost {
  /* OUTER: position + tilt only. JS writes the full transform string per
     frame (translate3d for centering + rotate for velocity tilt). Visual
     styling (shadow, bg, radius) lives on the inner so the pickup keyframe
     can animate them without competing with the per-frame position writes. */
  position: fixed;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  pointer-events: none;
  z-index: 2147483647;
  transform: translate3d(-50%, -50%, 0) rotate(0deg);
  will-change: transform;
}
.pb-reorder-ghost-scope {
  /* INNER: real box (replaces "display: contents !important"). Carries
     transform: scale + shadow + bg + radius so the pickup keyframe can
     target them. Fills the outer 100%/100% so the JS-set outer width/height
     determines the visible size. Flex-centers the cloned source content. */
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  overflow: hidden;
  white-space: nowrap;
  transform: scale(1.02);
  transform-origin: center center;
  box-shadow: 0 14px 32px rgba(15, 23, 42, 0.18), 0 0 0 2px var(--pb-edit-outline-active);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.96);
  transition: box-shadow 80ms ease-out, transform 100ms ease-out;
}
.pb-reorder-ghost-scope--springing {
  animation: pb-reorder-ghost-pickup 180ms cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}
/* Phase 19: snap-back on cancel. Transitions left/top/transform on the
   outer over 160ms ease-in. The inner stays at resting scale(1.02). */
.pb-reorder-ghost--snapping-back {
  transition: left 160ms ease-in, top 160ms ease-in, transform 160ms ease-in;
}
/* Phase 20: View Transitions API speed override. Browser default is ~500ms;
   220ms keeps the morph snappy and matches the perceived speed of v3's
   other micro-interactions (pickup 180ms, snap-back 160ms, pull 140ms). */
::view-transition-group(*) {
  animation-duration: 220ms;
  animation-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}
::view-transition-old(*), ::view-transition-new(*) {
  animation-duration: 220ms;
}
@keyframes pb-reorder-ghost-pickup {
  0%   { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(15, 23, 42, 0); }
  60%  { transform: scale(1.04); }
  100% { transform: scale(1.02); box-shadow: 0 14px 32px rgba(15, 23, 42, 0.18), 0 0 0 2px var(--pb-edit-outline-active); }
}
.pb-reorder-ghost li {
  list-style: none !important;
}
.pb-reorder-ghost .cs-li {
  margin-left: 0 !important;
  margin-right: 0 !important;
  opacity: 1 !important;
  transform: none !important;
  transition: none !important;
}
.pb-reorder-ghost a {
  pointer-events: none;
}
[data-pb-reorder-shift] {
  transition: transform 160ms cubic-bezier(0.2, 0, 0, 1);
  will-change: transform;
}
/* Phase 21: translate3d promotes shifting siblings to their own GPU
   compositing layer, eliminating mid-drag paint cost on the row. */
[data-pb-reorder-shift="prev"]:not([data-pb-reorder-pull]) {
  transform: translate3d(var(--pb-reorder-offset, calc(-1 * var(--pb-reorder-shift, 0px))), 0, 0);
}
[data-pb-reorder-shift="next"]:not([data-pb-reorder-pull]) {
  transform: translate3d(var(--pb-reorder-offset, var(--pb-reorder-shift, 0px)), 0, 0);
}
/* Phase 18: magnetic pull stacks an extra ±4px on the per-slot offset when
   the cursor approaches the gap midpoint. Compound selectors ONLY — never
   apply pull without a matching shift (prevents --pb-reorder-shift inheritance
   leak onto non-shifted siblings). */
[data-pb-reorder-shift="prev"][data-pb-reorder-pull="left"] {
  transform: translate3d(calc(var(--pb-reorder-offset, calc(-1 * var(--pb-reorder-shift, 0px))) - var(--pb-reorder-pull, 4px)), 0, 0);
}
[data-pb-reorder-shift="next"][data-pb-reorder-pull="right"] {
  transform: translate3d(calc(var(--pb-reorder-offset, var(--pb-reorder-shift, 0px)) + var(--pb-reorder-pull, 4px)), 0, 0);
}
/* Phase 2 (Tier 3): vertical-axis reorder. The drag engine stamps
   data-pb-reorder-axis="y" on the parent for column-stacked groups (FAQ items,
   footer link lists). These rules translate on Y instead of X and, being one
   attribute more specific than the default (x) rules above, win the cascade so
   no translateX leaks onto a vertical drag. Pull tokens left/right map to the
   before-side/after-side just as on the horizontal axis. */
[data-pb-reorder-axis="y"] > [data-pb-reorder-shift="prev"]:not([data-pb-reorder-pull]) {
  transform: translate3d(0, var(--pb-reorder-offset, calc(-1 * var(--pb-reorder-shift, 0px))), 0);
}
[data-pb-reorder-axis="y"] > [data-pb-reorder-shift="next"]:not([data-pb-reorder-pull]) {
  transform: translate3d(0, var(--pb-reorder-offset, var(--pb-reorder-shift, 0px)), 0);
}
[data-pb-reorder-axis="y"] > [data-pb-reorder-shift="prev"][data-pb-reorder-pull="left"] {
  transform: translate3d(0, calc(var(--pb-reorder-offset, calc(-1 * var(--pb-reorder-shift, 0px))) - var(--pb-reorder-pull, 4px)), 0);
}
[data-pb-reorder-axis="y"] > [data-pb-reorder-shift="next"][data-pb-reorder-pull="right"] {
  transform: translate3d(0, calc(var(--pb-reorder-offset, var(--pb-reorder-shift, 0px)) + var(--pb-reorder-pull, 4px)), 0);
}
/* Grid-axis reorder slides its neighbours exactly as a row does, but "one slot
   back" in a wrapped grid can cross a row boundary, so the shift is 2D: the
   engine writes both components from the frozen base rects. Declared after the
   pull rules so it wins the cascade at equal specificity; grid mode never
   stamps a pull token, so there is nothing to stack here. */
[data-pb-reorder-axis="grid"] > [data-pb-reorder-shift] {
  transform: translate3d(var(--pb-reorder-offset-x, 0px), var(--pb-reorder-offset-y, 0px), 0);
}
/* Pinned grids (cards / mosaic galleries) whose cells are NOT in DOM reading
   order cannot slide sensibly — they outline the drop-target cell instead while
   the ghost tracks the cursor; the commit renumbers ordinal classes to reflow. */
[data-pb-reorder-grid-target] {
  outline: 2px dashed var(--pb-edit-outline-active, #6366f1);
  outline-offset: 2px;
  border-radius: 0.25rem;
}
/* Cross-container move target. Existing items receive a directional edge;
   empty compatible containers receive an inset outline for an inside drop. */
[data-pb-move-target] {
  outline: 2px solid var(--pb-edit-outline-active, #6366f1);
  outline-offset: 3px;
}
[data-pb-move-target][data-pb-move-edge="top"] {
  box-shadow: inset 0 3px 0 var(--pb-edit-outline-active, #6366f1);
}
[data-pb-move-target][data-pb-move-edge="bottom"] {
  box-shadow: inset 0 -3px 0 var(--pb-edit-outline-active, #6366f1);
}
[data-pb-move-target][data-pb-move-edge="left"] {
  box-shadow: inset 3px 0 0 var(--pb-edit-outline-active, #6366f1);
}
[data-pb-move-target][data-pb-move-edge="right"] {
  box-shadow: inset -3px 0 0 var(--pb-edit-outline-active, #6366f1);
}
.pb-reorder-ghost[data-pb-cross-container] {
  box-shadow: 0 16px 36px rgba(15, 23, 42, 0.22), 0 0 0 2px var(--pb-edit-outline-active, #6366f1);
}
@media (prefers-reduced-motion: reduce) {
  .pb-reorder-ghost, [data-pb-reorder-shift] { transition: none !important; }
  .pb-reorder-ghost-scope { transition: none; transform: scale(1.0); }
  .pb-reorder-ghost-scope--springing { animation: none; }
  .pb-reorder-ghost--snapping-back { transition: none; }
  ::view-transition-group(*),
  ::view-transition-old(*),
  ::view-transition-new(*) { animation-duration: 1ms; }
}
[data-pb-editable] { cursor: grab; }
/* Same iOS latch as the hover nameplate above: a tap leaves this dashed
   outline on an element the user is no longer pointing at, with no JS able to
   clear it. Selection is the touch equivalent of hover (D5) and is drawn
   separately. */
@media (hover: hover) and (pointer: fine) {
  [data-pb-editable]:hover { outline: 2px dashed var(--pb-edit-outline); cursor: grab; }
}
[data-pb-editable]:active { cursor: grabbing; }
/* Pick mode: every click tags rather than selects, so nothing should look
 * draggable. The existing hover outlines stay as the "what will I tag" cue. */
body.pb-ai-pick-mode [data-pb-editable],
body.pb-ai-pick-mode [data-pb-instance] { cursor: crosshair; }
/* Comment mode (F5c): same reasoning — a click pins feedback, so the grab
 * cursor above would promise a drag that cannot happen. The parent sets the
 * crosshair on the iframe ELEMENT and the overlay, but that never reaches
 * this document, which is why the mode is mirrored in here via a body class
 * the bridge sets. Descendants are covered too: in comment mode a link is
 * not going to navigate, so a pointer cursor on it would be a lie. */
body.pb-comment-mode [data-pb-editable],
body.pb-comment-mode [data-pb-instance],
body.pb-comment-mode [data-pb-instance] * { cursor: crosshair; }
/* Tier 4 Phase 4: honest "not editable" cue. Transient attr set by the iframe
 * hover reporter on regions with no editable and no adapter coverage — a muted
 * grey dashed outline (vs the blue editable outline) + not-allowed cursor, so an
 * inert region is visually distinct from a misclick. Never persisted. */
[data-pb-inert-hover] {
  cursor: not-allowed;
  outline: 1.5px dashed rgba(100, 116, 139, 0.45);
  outline-offset: 2px;
}
/* Tier 4 Phase 5: inert-family editables. Embeds (iframes) would swallow
 * clicks into their own document, so edit mode disables their pointer events —
 * the bridge hit-tests the click point against embed rects instead. This whole
 * stylesheet is disabled in preview mode, so maps stay interactive there.
 * Field/media editables keep the standard editable cursor affordance. */
[data-pb-editable="embed"] { pointer-events: none; }
[data-pb-editable="field"],
[data-pb-editable="media"] { cursor: grab; }
/* Places tagged as context for the agent chat. Teal so a tagged element reads
 * as distinct from the blue selection and the purple section outline; the
 * chip in the composer uses the same accent. Declared before the editing and
 * selection rules so those keep winning on an element that is both. */
[data-pb-editable][data-pb-ai-context] {
  outline: 2px solid var(--pb-accent-ai);
  outline-offset: 2px;
}
/* ::before and ::after on the wrapper are already the nameplate and the
 * section outline, so a tagged section gets an inset shadow instead. */
[data-pb-instance][data-pb-ai-context] {
  box-shadow: inset 0 0 0 2px var(--pb-accent-ai);
}
.pb-ai-context-marker[data-pb-ai-context-marker]::before {
  content: 'Agent context · ' attr(data-pb-label);
  position: absolute;
  right: auto;
  bottom: calc(100% + 4px);
  left: 0;
  z-index: 21;
  max-width: 220px;
  overflow: hidden;
  padding: 2px 8px;
  border-radius: var(--pb-radius-sm);
  background: var(--pb-accent-ai);
  color: var(--pb-on-accent);
  font: 600 11px/1.4 system-ui, sans-serif;
  pointer-events: none;
  text-overflow: ellipsis;
  white-space: nowrap;
}
[data-pb-editable].pb-editing {
  outline: 2px solid var(--pb-edit-outline-active);
  outline-offset: 2px;
}
[data-pb-editable][data-pb-selected-target]:not(.pb-editing) {
  outline: 2px solid var(--pb-edit-outline-active);
  outline-offset: 2px;
}
/* 16C1 — co-selected repeat-root siblings. The lead keeps the solid
   data-pb-selected-target outline above; members read as part of the same set
   without competing with it, mirroring the section-level dashed treatment.
   Members render no label pill — N stacked pills are unreadable. */
[data-pb-element-multi]:not(.pb-editing) {
  outline: 2px dashed var(--pb-edit-outline-active);
  outline-offset: 2px;
}
[data-pb-drop-target] {
  outline: 3px dashed var(--pb-edit-outline-active) !important;
  outline-offset: 3px;
  filter: brightness(1.06);
}
[data-pb-editable="text"].pb-editing,
[data-pb-editable="text"][contenteditable="true"],
[data-pb-editable="text"][contenteditable="true"] * {
  cursor: text !important;
  -webkit-user-select: text !important;
  user-select: text !important;
  -webkit-user-drag: none !important;
}
[data-pb-label-position-anchor],
[data-pb-editable="text"].pb-editing[data-pb-edit-pill] {
  position: relative;
}
.pb-pill-marker {
  position: absolute;
  z-index: 19;
  width: 0;
  height: 0;
  pointer-events: none;
  overflow: visible;
}
${PILL_SELECTORS} {
  position: absolute;
  display: inline-block !important;
  top: auto !important;
  right: auto !important;
  bottom: calc(100% + 8px) !important;
  left: 0 !important;
  width: auto !important;
  height: auto !important;
  min-width: 0 !important;
  min-height: 0 !important;
  max-width: none !important;
  max-height: none !important;
  margin: 0 !important;
  float: none !important;
  order: 0 !important;
  transform: none !important;
  animation: none !important;
  transition: none !important;
  clip: auto !important;
  clip-path: none !important;
  opacity: 1 !important;
  overflow: visible !important;
  box-sizing: border-box !important;
  z-index: 20 !important;
  pointer-events: none !important;
  white-space: nowrap;
  background: var(--pb-accent);
  color: #fff;
  border-radius: 999px;
  font-size: 11px;
  font-family: system-ui, sans-serif;
  line-height: 1.4;
  font-weight: 600;
  text-transform: none !important;
  letter-spacing: 0.01em;
  padding: 2px 8px;
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.18);
}
${PILL_FLIP_SELECTORS} {
  top: calc(100% + 8px) !important;
  bottom: auto !important;
}
${PILL_CONTENT_RULES}
${PILL_HOVER_CONTENT_RULES}
[data-pb-editable="text"].pb-edit-empty:not(.pb-editing):not([contenteditable="true"]) {
  position: relative;
  min-height: 1em;
}
[data-pb-editable="text"].pb-edit-empty:not(.pb-editing):not([contenteditable="true"])::before {
  content: 'Type text...';
  color: rgba(148, 163, 184, 0.9);
  font-style: italic;
  pointer-events: none;
}
.pb-section-wrapper { position: relative; z-index: calc(var(--pb-section-count, 100) - var(--pb-section-index, 0)); }
[data-pb-instance] { position: relative; }
[data-pb-instance][data-pb-section-selected]::after,
[data-pb-instance][data-pb-section-hovered]:not([data-pb-section-selected]):not([data-pb-section-element-hovered])::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 18;
  pointer-events: none;
  box-sizing: border-box;
  border: 2px solid var(--pb-accent-section);
  border-radius: var(--pb-radius-sm);
}
[data-pb-instance][data-pb-section-hovered]:not([data-pb-section-selected]):not([data-pb-section-element-hovered])::after {
  border-color: var(--pb-accent-section-subtle);
}
/* 16A1 — co-selected members of a multi-selection. The lead keeps the solid
   data-pb-section-selected treatment; members read as part of the set without
   competing with it. Only the lead renders a label pill (N stacked pills are
   unreadable) — members have no ::before rule on purpose. */
[data-pb-instance][data-pb-section-multi]::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 18;
  pointer-events: none;
  box-sizing: border-box;
  border: 2px dashed var(--pb-accent-section);
  border-radius: var(--pb-radius-sm);
}
[data-pb-instance][data-pb-section-selected][data-pb-multi-count]::before {
  content: attr(data-pb-label) ' · ' attr(data-pb-multi-count);
}
[data-pb-instance][data-pb-section-first]::after {
  border-top-left-radius: var(--pb-canvas-radius);
  border-top-right-radius: var(--pb-canvas-radius);
}
[data-pb-instance][data-pb-section-last]::after {
  border-bottom-left-radius: var(--pb-canvas-radius);
  border-bottom-right-radius: var(--pb-canvas-radius);
}
[data-pb-instance][data-pb-section-selected]::before,
[data-pb-instance][data-pb-section-hovered]:not([data-pb-section-selected]):not([data-pb-section-element-hovered])::before {
  content: attr(data-pb-label);
  position: absolute;
  top: 0;
  left: 0;
  transform: translateY(-100%);
  z-index: 19;
  pointer-events: none;
  white-space: nowrap;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  background: var(--pb-accent-section);
  color: var(--pb-on-accent);
  border-radius: var(--pb-radius-sm) var(--pb-radius-sm) 0 0;
  font-size: var(--pb-font-sm);
  font-family: system-ui, sans-serif;
  line-height: 1.4;
  font-weight: 500;
  padding: var(--pb-space-1) var(--pb-space-4);
}
@media (prefers-reduced-motion: reduce) {
${PILL_SELECTORS} {
    transition: none !important;
  }
}

/* Flash animation — brief highlight pulse on section after select */
@keyframes pb-section-flash {
  0%   { box-shadow: inset 0 0 0 0 var(--pb-accent-glow); }
  40%  { box-shadow: inset 0 0 0 4px var(--pb-accent-glow); }
  100% { box-shadow: inset 0 0 0 0 transparent; }
}
.pb-section-flash { animation: pb-section-flash 0.8s ease-out; }
@media (prefers-reduced-motion: reduce) {
  .pb-section-flash { animation: none; }
}

/* Find & Replace — highlight pulse on a matched text run after navigate */
@keyframes pb-text-flash {
  0%   { background-color: transparent; box-shadow: 0 0 0 0 var(--pb-accent-glow); }
  40%  { background-color: var(--pb-accent-muted, rgba(0,120,212,0.18)); box-shadow: 0 0 0 6px var(--pb-accent-glow); }
  100% { background-color: transparent; box-shadow: 0 0 0 0 transparent; }
}
.pb-find-match-focus {
  animation: pb-text-flash 1s ease-out;
  border-radius: 2px;
}
@media (prefers-reduced-motion: reduce) {
  .pb-find-match-focus { animation: none; }
}

.pb-broken-img { opacity: 0.3; }

/* Phase 6 apply-and-verify: while the bridge reads a verify snapshot it stamps
 * data-pb-verifying on the element so mid-transition values (global-base.css
 * puts .3s color/background transitions on buttons) can't produce a false
 * "override didn't win". Removed synchronously after the read. */
[data-pb-verifying], [data-pb-verifying] * {
  transition: none !important;
}

/* Collab Phase 9: a peer holds this section's soft lock. The wrapper attrs
 * (data-pb-collab-locked / data-pb-collab-name) are render-generated by the
 * bridge (never stored HTML) and re-applied on bridge-ready. A tinted inset
 * outline (box-shadow, so it never fights the selection/hover ::after) plus a
 * nameplate carrying the holder's name via attr() (renders as text — safe). */
[data-pb-instance][data-pb-collab-locked] {
  box-shadow: inset 0 0 0 2px var(--pb-collab-lock-color, #d98324);
  border-radius: var(--pb-radius-sm);
}
[data-pb-instance][data-pb-collab-locked]::before {
  content: attr(data-pb-collab-name);
  position: absolute;
  top: 0;
  left: 0;
  z-index: 20;
  padding: 2px 8px;
  font: 600 11px/1.4 system-ui, sans-serif;
  color: #fff;
  background: var(--pb-collab-lock-color, #d98324);
  border-bottom-right-radius: var(--pb-radius-sm);
  pointer-events: none;
  white-space: nowrap;
}

/* Collab Phase 10: a peer is selecting/editing here (lighter than a hard lock).
 * A soft tinted ring in the peer's color, no nameplate (the cursor + avatar
 * carry identity). Suppressed when the section is also hard-locked. */
[data-pb-instance][data-pb-collab-presence]:not([data-pb-collab-locked]) {
  box-shadow: inset 0 0 0 2px var(--pb-collab-presence-color, #4c8bf5);
  border-radius: var(--pb-radius-sm);
}

/* Agent Review AR-6: changed-section identity stays inside the iframe, like
 * collab locks. The inset ring leaves selection/hover's ::after available and
 * the attr()-backed plate renders the supplied label as text. When context and
 * change state co-occur, the teal marker plate remains a separate host while
 * this violet ring steps inward, so neither state hides the other. */
[data-pb-instance][data-pb-section-changed] {
  box-shadow: inset 0 0 0 3px var(--pb-accent-section);
  border-radius: var(--pb-radius-sm);
}
[data-pb-instance][data-pb-section-changed][data-pb-ai-context] {
  box-shadow:
    inset 0 0 0 2px var(--pb-accent-ai),
    inset 0 0 0 5px var(--pb-accent-section);
}
[data-pb-instance][data-pb-section-changed]::before {
  content: attr(data-pb-change-label);
  position: absolute;
  top: 0;
  right: 0;
  left: auto;
  z-index: 20;
  max-width: 220px;
  overflow: hidden;
  padding: 2px 8px;
  border-bottom-left-radius: var(--pb-radius-sm);
  background: var(--pb-accent-section);
  color: var(--pb-on-accent);
  font: 600 11px/1.4 system-ui, sans-serif;
  pointer-events: none;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* CE-5 client content-edit mode. Gated on a body attribute the renderer stamps
 * only when state.flags.clientEditMode is set (the client editor page — the
 * builder never sets it), following the glass-vs-iframe doctrine: identity
 * visuals live in this document, driven by data-pb-* markers. Affordances
 * survive ONLY on text and image slots — everything else neither invites a
 * hover nor promises a drag, matching the parent interceptor that consumes
 * those clicks. */
body[data-pb-client-mode] [data-pb-editable]:not([data-pb-editable='text']):not([data-pb-editable='image']) {
  cursor: default !important;
}
@media (hover: hover) and (pointer: fine) {
  body[data-pb-client-mode]
    [data-pb-editable]:not([data-pb-editable='text']):not([data-pb-editable='image']):hover {
    outline: none !important;
    cursor: default !important;
  }
}
body[data-pb-client-mode] [data-pb-editable='text']:not([data-pb-client-locked]),
body[data-pb-client-mode] [data-pb-editable='image'] {
  cursor: pointer;
}
body[data-pb-client-mode] [data-pb-client-locked] {
  outline: none !important;
  cursor: default !important;
}
@media (hover: hover) and (pointer: fine) {
  body[data-pb-client-mode] [data-pb-client-locked]:hover {
    outline: none !important;
    cursor: default !important;
  }
}
/* No section identity chrome for clients: nameplates, hover/selected outlines
 * and change plates are the builder's vocabulary, not the client editor's. */
body[data-pb-client-mode] [data-pb-instance][data-pb-section-hovered],
body[data-pb-client-mode] [data-pb-instance][data-pb-section-selected],
body[data-pb-client-mode] [data-pb-instance][data-pb-section-changed] {
  box-shadow: none !important;
}
body[data-pb-client-mode] [data-pb-instance][data-pb-section-changed]::before,
body[data-pb-client-mode] [data-pb-instance][data-pb-section-hovered]::after,
body[data-pb-client-mode] [data-pb-instance][data-pb-section-selected]::after {
  content: none !important;
}
`;

/**
 * Returns the CSS the canvas iframe gets: EDITING_CSS and the editing-time nav rules.
 * Section controls and drag-and-drop are drawn by the glass overlay in the parent document.
 * The frame's own copies of them, which this function used to strip, were deleted at SYS-10.
 */
export function getEditingCSS() {
  return (
    EDITING_CSS +
    // Neutralize fixed/sticky nav while editing so canvas scroll stays stable.
    // In preview mode, #pb-editing is disabled and template nav behavior returns.
    // isolation:isolate restores the stacking context the neutralized header loses
    // when position:fixed;z-index:10000 becomes position:relative;z-index:auto —
    // without it, a template's negative-z-index bar (e.g. .cs-top-bar{z-index:-3})
    // sinks behind the header box and its editables stop hit-testing on top.
    `\n[data-pb-shared="navigation"] > *,
[data-pb-category="navigation"] > * {
  position: relative !important;
  top: auto !important;
  left: auto !important;
  right: auto !important;
  bottom: auto !important;
  inset: auto !important;
  z-index: auto !important;
  transform: none !important;
  margin-left: auto !important;
  margin-right: auto !important;
  isolation: isolate !important;
}

/* Deep-nested absolute toggles often override only bottom/right; global-base's
   generic [id^="dark-mode-toggle"] mobile rule then leaks top:50%/translateY,
   which beats the section's bottom and floats the toggle to the header center.
   Reset both here so the section's bottom/right anchoring wins. */
[data-pb-shared="navigation"] [id^="dark-mode-toggle"],
[data-pb-category="navigation"] [id^="dark-mode-toggle"] {
  top: auto !important;
  transform: none !important;
}

[data-pb-template-overlay="true"] {
  display: none !important;
}`
  );
}
